import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { ProductList } from '../../components/ProductList';
import { api } from '../../services/api';

export default function Products() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    code: '',
    description: '',
    purchasePrice: '',
    salePrice: '',
    quantity: '',
  });

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewProduct({
      name: '',
      code: '',
      description: '',
      purchasePrice: '',
      salePrice: '',
      quantity: '',
    });
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.companyId) {
      setError('Company ID not found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.createProduct({
        name: newProduct.name,
        code: newProduct.code,
        description: newProduct.description,
        purchasePrice: Number(newProduct.purchasePrice),
        salePrice: Number(newProduct.salePrice),
        quantity: Number(newProduct.quantity),
        companyId: user.companyId
      });
      
      handleCloseDialog();
      window.location.reload();
    } catch (err) {
      console.error('Error creating product:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" gutterBottom>
            {t.products}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Plus />}
            onClick={handleOpenDialog}
            sx={{ bgcolor: '#1F2937' }}
          >
            {t.addProduct}
          </Button>
        </Box>

        {user?.companyId ? (
          <Box sx={{ width: '100%', overflow: 'hidden' }}>
            <ProductList companyId={user.companyId} />
          </Box>
        ) : (
          <Alert severity="error">
            Aucun ID d'entreprise trouvé. Veuillez contacter l'administrateur.
          </Alert>
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{t.addProduct}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  autoFocus
                  name="code"
                  label={t.productCode}
                  type="text"
                  fullWidth
                  value={newProduct.code}
                  onChange={handleInputChange}
                  required
                />
                <TextField
                  name="name"
                  label={t.productName}
                  type="text"
                  fullWidth
                  value={newProduct.name}
                  onChange={handleInputChange}
                  required
                />
                <TextField
                  name="description"
                  label={t.description}
                  type="text"
                  fullWidth
                  multiline
                  rows={3}
                  value={newProduct.description}
                  onChange={handleInputChange}
                />
                <TextField
                  name="purchasePrice"
                  label={t.purchasePrice}
                  type="number"
                  fullWidth
                  value={newProduct.purchasePrice}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0 }}
                />
                <TextField
                  name="salePrice"
                  label={t.salePrice}
                  type="number"
                  fullWidth
                  value={newProduct.salePrice}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0 }}
                />
                <TextField
                  name="quantity"
                  label={t.quantity}
                  type="number"
                  fullWidth
                  value={newProduct.quantity}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0 }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>
                {t.cancel}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ bgcolor: '#1F2937' }}
              >
                {t.save}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
